import { useState, useEffect } from "react";
import { C, SERVICES } from "../data/constants";
import { postEnquiry } from "../hooks/useApi";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", eventType: "", eventDate: "", packageTier: "", message: "" });
  const [customItems, setCustomItems] = useState([]);
  const [estimatedTotal, setEstimatedTotal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("byoPayload");
    if (stored) {
      try {
        const p = JSON.parse(stored);
        setForm(prev => ({ ...prev, eventType: p.eventType || prev.eventType }));
        setCustomItems(p.customItems || []);
        setEstimatedTotal(p.estimatedTotal || "");
        sessionStorage.removeItem("byoPayload");
      } catch {}
    }
  }, []);

  const set = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await postEnquiry({ ...form, customItems, estimatedTotal, source: "website-contact" });
      setSubmitted(true);
      setForm({ name: "", email: "", whatsapp: "", eventType: "", eventDate: "", packageTier: "", message: "" });
      setCustomItems([]); setEstimatedTotal("");
    } catch (err) { setError(err.message || "Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  const inp = { width: "100%", padding: "11px 14px", background: "rgba(255,255,255,0.07)", border: `1px solid rgba(159,172,157,0.25)`, borderRadius: 8, color: C.white, fontSize: 14, fontFamily: "'Jost',sans-serif" };
  const lbl = { color: C.sand, fontSize: 11, letterSpacing: 1, display: "block", marginBottom: 6, textTransform: "uppercase" };

  return (
    <section id="contact" className="section-pad" style={{ background: C.dark }}>
      <div className="container">
        <div className="contact-layout">

          {/* Left info */}
          <div className="fade">
            <p style={{ color: C.blush, letterSpacing: 3, textTransform: "uppercase", fontSize: 11, marginBottom: 14 }}>Let's Create Together</p>
            <h2 style={{ fontSize: "clamp(28px,4vw,48px)", fontWeight: 300, color: C.white, marginBottom: 18 }}>Book Your Event</h2>
            <div style={{ width: 48, height: 1, background: C.blush, marginBottom: 24 }} />
            <p style={{ color: C.sand, lineHeight: 1.8, marginBottom: 32, fontSize: "clamp(13px,1.5vw,15px)" }}>
              Tell us about your special occasion and we'll get back to you within 24 hours with a personalised quote.
            </p>
            {[
              { icon: "📞", label: "Call / WhatsApp", val: "+27 686 753 020" },
              { icon: "✉️", label: "Email", val: "hello@momentsandmilestones.co.za" },
              { icon: "📍", label: "Based In", val: "Cape Town, South Africa" },
              { icon: "🕐", label: "Working Hours", val: "Mon – Sat: 8am – 7pm" },
            ].map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{c.icon}</span>
                <div>
                  <p style={{ color: C.slate, fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>{c.label}</p>
                  <p style={{ color: C.linen, fontSize: 14 }}>{c.val}</p>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 28, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[["Instagram","https://www.instagram.com/momentsandmilestones.co.za/?hl=en#"],["TikTok","https://www.tiktok.com/@momentsnmilestones?is_from_webapp=1&sender_device=pc"],["Facebook","https://facebook.com"],["WhatsApp","https://wa.me/27686753020"]].map(([label, href]) => (
                <a key={label} href={href} target="_blank" rel="noreferrer"
                  style={{ background: "rgba(255,255,255,0.1)", color: C.linen, padding: "7px 16px", borderRadius: 18, fontSize: 12, textDecoration: "none", border: `1px solid rgba(159,172,157,0.25)` }}>
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="fade">
            {submitted ? (
              <div style={{ background: "rgba(159,172,157,0.1)", border: `1px solid ${C.sage}`, borderRadius: 16, padding: "40px 32px", textAlign: "center" }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>✨</div>
                <h3 style={{ color: C.white, fontSize: "clamp(20px,3vw,26px)", marginBottom: 10, fontFamily: "'Cormorant Garamond',serif" }}>Thank You!</h3>
                <p style={{ color: C.sand, lineHeight: 1.8, marginBottom: 20 }}>We've received your enquiry and will be in touch within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} style={{ background: C.blush, color: C.white, border: "none", padding: "10px 24px", borderRadius: 22, cursor: "pointer", fontSize: 14 }}>
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                {[["name","Full Name","text",true],["email","Email Address","email",true],["whatsapp","WhatsApp Number","text",false],["eventDate","Event Date","date",false]].map(([field,label,type,req]) => (
                  <div key={field}>
                    <label style={lbl}>{label}{req ? " *" : ""}</label>
                    <input type={type} required={req} value={form[field]} onChange={set(field)} style={inp} />
                  </div>
                ))}
                <div>
                  <label style={lbl}>Event Type *</label>
                  <select required value={form.eventType} onChange={set("eventType")} style={{ ...inp, background: "#2C2826" }}>
                    <option value="">Select an event type…</option>
                    {SERVICES.map(s => <option key={s.title}>{s.title}</option>)}
                    <option>Custom / Build Your Own</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Package Tier (optional)</label>
                  <select value={form.packageTier} onChange={set("packageTier")} style={{ ...inp, background: "#2C2826" }}>
                    <option value="">No preference</option>
                    <option>Bronze</option><option>Silver</option><option>Gold</option>
                    <option>Custom / Build Your Own</option>
                  </select>
                </div>
                {customItems.length > 0 && (
                  <div style={{ background: "rgba(205,172,161,0.12)", border: "1px solid rgba(205,172,161,0.3)", borderRadius: 10, padding: "11px 14px" }}>
                    <p style={{ color: C.sand, fontSize: 12, marginBottom: 7 }}>Custom build — {customItems.length} items · {estimatedTotal}</p>
                    {customItems.slice(0, 4).map((item, i) => <p key={i} style={{ color: C.linen, fontSize: 12, lineHeight: 1.8 }}>• {item.name} — {item.price}</p>)}
                    {customItems.length > 4 && <p style={{ color: C.slate, fontSize: 12 }}>…and {customItems.length - 4} more</p>}
                  </div>
                )}
                <div>
                  <label style={lbl}>Your Vision / Message</label>
                  <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your dream event…" style={{ ...inp, resize: "vertical" }} />
                </div>
                {error && <p style={{ color: "#ff6b6b", fontSize: 13, background: "rgba(255,107,107,0.1)", padding: "9px 13px", borderRadius: 8 }}>{error}</p>}
                <button type="submit" disabled={submitting}
                  style={{ background: C.blush, color: C.white, border: "none", padding: "13px", borderRadius: 26, fontSize: 15, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.75 : 1, marginTop: 4 }}>
                  {submitting ? "Sending…" : "Send My Enquiry"}
                </button>
                <p style={{ color: C.slate, fontSize: 12, textAlign: "center" }}>We respond within 24 hours · Cape Town</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}